import { useState, useEffect, useRef } from "react";
import { apiGet, apiPost, apiPut } from "@/lib/api";
import { ShieldCheck, LogIn, LogOut, Search, UserX, Clock, Car, User, AlertTriangle, CheckCircle, XCircle } from "lucide-react";

const CATEGORIES = ["Guest", "Contractor", "Delivery", "Domestic Helper"];

export default function SecurityDashboard() {
  const [tab, setTab] = useState("checkin");
  const [active, setActive] = useState([]);
  const [todayLog, setTodayLog] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadActive();
    loadTodayLog();
  }, []);

  async function loadActive() {
    try { const r = await apiGet("/visits/active"); setActive(r.data); } catch {}
  }
  async function loadTodayLog() {
    const today = new Date().toISOString().slice(0, 10);
    try { const r = await apiGet(`/visits?date_from=${today}&date_to=${today}`); setTodayLog(r.data); } catch {}
  }

  return (
    <div>
      <div style={{ display: "flex", gap: 8, marginBottom: 20, flexWrap: "wrap" }}>
        {[["checkin", "Check-In", <LogIn size={13} />], ["active", `On-Site (${active.length})`, <ShieldCheck size={13} />], ["log", "Today's Log", <Clock size={13} />]].map(([key, label, icon]) => (
          <button key={key} onClick={() => setTab(key)}
            className={`btn ${tab === key ? "btn-primary" : ""}`}
            style={{ display: "flex", alignItems: "center", gap: 5 }}>
            {icon}{label}
          </button>
        ))}
      </div>

      {tab === "checkin" && <CheckInTab onSuccess={() => { loadActive(); loadTodayLog(); }} />}
      {tab === "active" && <ActiveVisitorsTab visitors={active} onCheckout={async (id) => { await apiPut(`/visits/${id}/checkout`); loadActive(); loadTodayLog(); }} />}
      {tab === "log" && <LogTab logs={todayLog} />}
    </div>
  );
}

// ── Check-In Tab ─────────────────────────────────────────────────────────────
function CheckInTab({ onSuccess }) {
  const [mode, setMode] = useState("qr"); // qr | manual
  const [qrInput, setQrInput] = useState("");
  const [resolved, setResolved] = useState(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ visitor_name: "", visitor_ic: "", visitor_phone: "", category: "Guest", purpose: "", unit: "", vehicle_plate: "", expected_duration_mins: 120, requires_approval: false });
  // vehicle_plate is required
  const qrRef = useRef(null);

  function upd(field) { return e => setForm(f => ({ ...f, [field]: e.target.value })); }

  async function resolveQR() {
    setError(""); setResolved(null);
    const token = qrInput.trim();
    if (!token) return;
    try {
      const r = await apiGet(`/invitations/resolve/${token}`);
      const d = r.data.details;
      setResolved(r.data);
      setForm(f => ({
        ...f,
        visitor_name: d.visitor_name, visitor_ic: d.visitor_ic,
        visitor_phone: d.visitor_phone, category: d.category,
        purpose: d.purpose, unit: d.unit,
        expected_duration_mins: d.expected_duration_mins ?? 120,
      }));
      setSuccess("");
    } catch (e) {
      setError(e.response?.data?.detail ?? "Invalid or expired QR code");
    }
  }

  const isGroupQr = resolved?.details?.group?.enabled === true;

  async function handleCheckin(e) {
    e.preventDefault();
    setError(""); setSuccess("");
    // Group QR guests may arrive without their own vehicle
    if (!isGroupQr && !form.vehicle_plate.trim()) { setError("Vehicle plate is required."); return; }
    setSubmitting(true);
    try {
      await apiPost("/visits/checkin", {
        ...form,
        qr_token: mode === "qr" ? qrInput.trim() : undefined,
        expected_duration_mins: Number(form.expected_duration_mins),
      });
      setSuccess(`${form.visitor_name} checked in successfully!`);
      setQrInput(""); setResolved(null);
      setForm({ visitor_name: "", visitor_ic: "", visitor_phone: "", category: "Guest", purpose: "", unit: "", vehicle_plate: "", expected_duration_mins: 120, requires_approval: false });
      onSuccess();
    } catch (e) {
      setError(e.response?.data?.detail ?? "Check-in failed");
    } finally { setSubmitting(false); }
  }

  return (
    <div style={{ maxWidth: 560 }}>
      <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
        <button className={`btn ${mode === "qr" ? "btn-primary" : ""}`} onClick={() => { setMode("qr"); setError(""); setResolved(null); }}>QR Code</button>
        <button className={`btn ${mode === "manual" ? "btn-primary" : ""}`} onClick={() => { setMode("manual"); setError(""); setResolved(null); }}>Walk-In Manual</button>
      </div>

      {mode === "qr" && (
        <div style={{ marginBottom: 16 }}>
          <label className="form-label">Scan or paste QR code token</label>
          <div style={{ display: "flex", gap: 8 }}>
            <input ref={qrRef} className="form-input" style={{ flex: 1 }} placeholder="QR token — hardware scanner auto-fills this"
              value={qrInput} onChange={e => setQrInput(e.target.value)}
              onKeyDown={e => e.key === "Enter" && resolveQR()} autoFocus />
            <button className="btn btn-primary" onClick={resolveQR}>Verify</button>
          </div>
          {resolved && (
            <div style={{ marginTop: 10, padding: "10px 12px", background: "var(--success-light, #f0fdf4)", border: "1px solid #86efac", borderRadius: 6, fontSize: 12 }}>
              <CheckCircle size={12} style={{ marginRight: 5, color: "#16a34a", verticalAlign: "middle" }} />
              <b>{resolved.details.visitor_name}</b> · {resolved.details.category} · expires {new Date(resolved.expires_at).toLocaleString()}
              {resolved.users && <span style={{ marginLeft: 8, color: "var(--text2)" }}>Host: {resolved.users.name} ({resolved.users.unit})</span>}
              {resolved.details.group?.enabled && (
                <div style={{ marginTop: 4, fontWeight: 600, color: "#166534" }}>
                  Group QR — {resolved.details.uses_count ?? 0} of {resolved.details.max_uses} visitors checked in
                  ({resolved.details.max_uses - (resolved.details.uses_count ?? 0)} remaining)
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {error && <div className="auth-error" style={{ marginBottom: 12 }}>{error}</div>}
      {success && <div className="auth-success" style={{ marginBottom: 12 }}>{success}</div>}

      <form onSubmit={handleCheckin}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
          <div className="form-group">
            <label className="form-label">Full Name *</label>
            <input className="form-input" value={form.visitor_name} onChange={upd("visitor_name")} required placeholder="Visitor full name" />
          </div>
          <div className="form-group">
            <label className="form-label">IC / Passport</label>
            <input className="form-input" value={form.visitor_ic} onChange={upd("visitor_ic")} placeholder="e.g. 900101-01-1234" />
          </div>
          <div className="form-group">
            <label className="form-label">Phone</label>
            <input className="form-input" value={form.visitor_phone} onChange={upd("visitor_phone")} placeholder="+60 12-345 6789" />
          </div>
          <div className="form-group">
            <label className="form-label" style={{ color: "var(--brand)", fontWeight: 600 }}>
              Vehicle Plate {!isGroupQr && <span style={{ color: "var(--danger)" }}>*</span>}
              {isGroupQr && <span style={{ fontWeight: 400, color: "var(--text2)" }}> (optional for group)</span>}
            </label>
            <input className="form-input" value={form.vehicle_plate} onChange={upd("vehicle_plate")} placeholder="e.g. WXY 1234" required={!isGroupQr} style={{ borderColor: !isGroupQr && !form.vehicle_plate ? "var(--danger)" : undefined }} />
          </div>
          <div className="form-group">
            <label className="form-label">Category *</label>
            <select className="form-input" value={form.category} onChange={upd("category")} required>
              {CATEGORIES.map(c => <option key={c}>{c}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Visiting Unit *</label>
            <input className="form-input" value={form.unit} onChange={upd("unit")} required placeholder="e.g. No. 12" />
          </div>
          <div className="form-group">
            <label className="form-label">Purpose *</label>
            <input className="form-input" value={form.purpose} onChange={upd("purpose")} required placeholder="e.g. Family visit" />
          </div>
          <div className="form-group">
            <label className="form-label">Expected Duration (min)</label>
            <input type="number" className="form-input" value={form.expected_duration_mins} onChange={upd("expected_duration_mins")} min={10} max={1440} />
          </div>
        </div>
        <div className="form-group" style={{ marginBottom: 14 }}>
          <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 13 }}>
            <input type="checkbox" checked={form.requires_approval} onChange={e => setForm(f => ({ ...f, requires_approval: e.target.checked }))} />
            Require resident approval before entry
          </label>
        </div>
        <button type="submit" className="btn btn-primary" style={{ width: "100%" }} disabled={submitting}>
          <LogIn size={13} style={{ marginRight: 5 }} />{submitting ? "Processing..." : "Check In Visitor"}
        </button>
      </form>
    </div>
  );
}

// ── Active Visitors Tab ────────────────────────────────────────────────────────
function ActiveVisitorsTab({ visitors, onCheckout }) {
  if (!visitors.length) return (
    <div style={{ color: "var(--text2)", fontSize: 13, padding: "20px 0" }}>No visitors currently on-site.</div>
  );

  return (
    <div>
      <div style={{ fontSize: 12, color: "var(--text2)", marginBottom: 12 }}>{visitors.length} visitor(s) on-site</div>
      {visitors.map(v => {
        const d = v.details;
        return (
          <div key={v.id} style={{ background: "var(--surface)", border: v.is_overstay ? "1px solid var(--pending)" : "1px solid var(--border)", borderRadius: 8, padding: "12px 14px", marginBottom: 10, display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 10 }}>
            <div>
              <div style={{ fontWeight: 600, fontSize: 14, display: "flex", alignItems: "center", gap: 6 }}>
                <User size={13} />
                {d.visitor_name}
                <span style={{ fontSize: 10, padding: "1px 6px", borderRadius: 10, background: "var(--brand-light)", color: "var(--brand)" }}>{d.category}</span>
                {v.is_overstay && <span style={{ fontSize: 10, padding: "1px 6px", borderRadius: 10, background: "var(--pending-light)", color: "var(--pending)" }}><AlertTriangle size={9} style={{ marginRight: 2 }} />Overstay</span>}
              </div>
              <div style={{ fontSize: 12, color: "var(--text2)", marginTop: 3 }}>
                Unit: {d.unit} · {d.purpose}
              </div>
              <div style={{ fontSize: 11, color: "var(--text2)", marginTop: 2, display: "flex", gap: 10 }}>
                <span><Clock size={10} style={{ marginRight: 3 }} />{v.elapsed_mins}min / {d.expected_duration_mins}min</span>
                {d.vehicle_plate && (
                  <span style={{ background: "#fef9c3", color: "#854d0e", padding: "1px 6px", borderRadius: 4, fontWeight: 700, letterSpacing: 1 }}>
                    <Car size={10} style={{ marginRight: 3 }} />{d.vehicle_plate}
                  </span>
                )}
              </div>
            </div>
            <button className="btn" style={{ fontSize: 12 }} onClick={() => onCheckout(v.id)}>
              <LogOut size={12} style={{ marginRight: 4 }} />Check Out
            </button>
          </div>
        );
      })}
    </div>
  );
}

// ── Log Tab ────────────────────────────────────────────────────────────────────
function LogTab({ logs }) {
  const STATUS_COLOR = { checked_in: "#16a34a", checked_out: "var(--text2)", overstay: "#f59e0b", denied: "#dc2626", pending_approval: "#7c3aed", approved: "#16a34a" };

  if (!logs.length) return <div style={{ color: "var(--text2)", fontSize: 13, padding: "20px 0" }}>No visits today.</div>;

  return (
    <div>
      <div style={{ fontSize: 12, color: "var(--text2)", marginBottom: 12 }}>{logs.length} visit(s) today</div>
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
          <thead>
            <tr style={{ borderBottom: "1px solid var(--border)", textAlign: "left" }}>
              {["Visitor", "Unit", "Plate", "Category", "Purpose", "Check-In", "Check-Out", "Status"].map(h => (
                <th key={h} style={{ padding: "6px 8px", fontWeight: 600, fontSize: 11, color: "var(--text2)", whiteSpace: "nowrap" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {logs.map(v => {
              const d = v.details;
              return (
                <tr key={v.id} style={{ borderBottom: "1px solid var(--border-light)" }}>
                  <td style={{ padding: "7px 8px", fontWeight: 500 }}>{d.visitor_name}</td>
                  <td style={{ padding: "7px 8px" }}>{d.unit}</td>
                  <td style={{ padding: "7px 8px" }}>
                    {d.vehicle_plate ? (
                      <span style={{ background: "#fef9c3", color: "#854d0e", padding: "2px 7px", borderRadius: 4, fontWeight: 700, fontSize: 11, letterSpacing: 1 }}>{d.vehicle_plate}</span>
                    ) : "—"}
                  </td>
                  <td style={{ padding: "7px 8px" }}>{d.category}</td>
                  <td style={{ padding: "7px 8px" }}>{d.purpose}</td>
                  <td style={{ padding: "7px 8px", whiteSpace: "nowrap" }}>{d.checked_in_at ? new Date(d.checked_in_at).toLocaleTimeString("en-MY", { hour: "2-digit", minute: "2-digit" }) : "—"}</td>
                  <td style={{ padding: "7px 8px", whiteSpace: "nowrap" }}>{d.checked_out_at ? new Date(d.checked_out_at).toLocaleTimeString("en-MY", { hour: "2-digit", minute: "2-digit" }) : "—"}</td>
                  <td style={{ padding: "7px 8px" }}>
                    <span style={{ fontSize: 10, padding: "2px 6px", borderRadius: 10, color: STATUS_COLOR[v.status] ?? "var(--text2)", background: "var(--brand-light)", fontWeight: 600 }}>
                      {v.status.replace("_", " ")}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
